import * as React from 'react';
import { Redirect } from 'react-router-dom';

import Message from 'antd/lib/message';
import Modal from 'antd/lib/modal';
import loadable from '@loadable/component';
import { FormikProps } from 'formik';

import { loadableDelay } from '@common/react/loadable/loadableSettings';

import Loader from '@common/react/components/Core/LoadingProvider/Loader';

import { BaseInvite } from '@commonTuna/react/objects/BaseInvite';
import QuestionnaireForm from '@commonTuna/react/components/UI/QuestionnaireForm/QuestionnaireForm';
import PatientSignature from '@commonTuna/react/components/UI/QuestionnaireForm/PatientSignature';
import { BaseTestSession } from '@commonTuna/react/objects/BaseTestSession';
import QuestionnaireButtons from '@commonTuna/react/components/UI/QuestionnaireForm/QuestionnaireButtons';

import { User } from '@app/objects/User';
import { request } from '@app/components/Api';

const params = {
	fallback: <Loader />,
};

const ConsentFormSwitcher = loadable(() =>
	loadableDelay(import(/* webpackChunkName: "ConsentFormSwitcher" */ '@commonTuna/react/components/UI/ConsentForm/ConsentFormSwitcher')), params);

interface ComponentProps {
	user: User;
	invites: Array<BaseInvite>;
	afterSubmit?: (userData?: User) => void;
}

interface QueryParams {
	inviteId: number;
	templateSnapshotId: number;
	appointmentId: number;
	appointmentProcedureId: number;
	diseaseId: number;
	patientId: number;
}

interface ComponentState {
	activeInviteIndex: number | null;
	queryParams: QueryParams;
}

export const RegistrationFillForms: React.FC<ComponentProps> = (props) => {
	const [session, setSession] = React.useState<BaseTestSession | null>(null);
	const [hasSigh, setHasSign] = React.useState<boolean>(false);
	const [copyForwardDisabled, setCopyForwardDisabled] = React.useState<boolean>(false);
	const [redirect, setRedirect] = React.useState<boolean>(false);
	const [state, setState] = React.useState<ComponentState>({
		activeInviteIndex: null,
		queryParams: {
			inviteId: 0,
			templateSnapshotId: 0,
			appointmentId: 0,
			appointmentProcedureId: 0,
			diseaseId: 0,
			patientId: 0,
		},
	});
	const [signatureSrc, setSignatureSrc] = React.useState<string | undefined>('');
	const [form, setForm] = React.useState<FormikProps<any> | null>(null);

	React.useEffect(() => {
		if (props.invites?.length > 0) {
			showModal(props.invites);
		} else {
			props.afterSubmit && props.afterSubmit(props.user);
		}
	}, []);

	React.useEffect(() => {
		if (state.queryParams.diseaseId > 0) {
			loadTestSession();
		}
	}, [state.queryParams.diseaseId]);

	const showModal = (inviteList: Array<BaseInvite>) => {
		Modal.confirm({
			title: 'Do you want to fill forms?',
			content: '',
			okText: 'Yes',
			okType: 'danger',
			cancelText: 'No',
			onOk() {
				let index = 0;
				while ((index < inviteList.length) && inviteList[index].answered) {
					index++;
				}
				if (index < inviteList.length) {
					setState({
						activeInviteIndex: index,
						queryParams: {
							...state.queryParams,
							inviteId: inviteList[index].id,
							templateSnapshotId: inviteList[index].templateSnapshotId,
							appointmentId: inviteList[index].appointmentId,
							diseaseId: inviteList[index].diseaseId,
							patientId: inviteList[index].patientId,
						},
					});
				} else {
					Modal.success({
						title: 'All forms are already filled out',
						onOk: () => props.afterSubmit && props.afterSubmit(props.user),
					});
				}
			},
			onCancel() {
				props.afterSubmit && props.afterSubmit(props.user);
			},
		});
	};

	const loadTestSession = () => {
		request<BaseTestSession>('getTestSession', {
			...state.queryParams,
			withDisease: true,
		}).then((res) => {
			setSession(res);
			setSignatureSrc(res.files?.list[0]?.file?.src);
			setCopyForwardDisabled(!res.oldTestSessionId || (res.oldTestSessionId <= 0));
		}).catch((err) => {
			Message.error(err);
		});
	};

	const setNextInvite = () => {
		let nextIndex = ((state.activeInviteIndex !== null) && (state.activeInviteIndex >= 0)) ? state.activeInviteIndex + 1 : -1;
		while ((nextIndex > 0) && (nextIndex < props.invites.length) && props.invites[nextIndex].answered) {
			nextIndex++;
		}
		if ((nextIndex > 0) && (nextIndex < props.invites.length)) {
			setState({
				activeInviteIndex: nextIndex,
				queryParams: {
					...state.queryParams,
					inviteId: props.invites[nextIndex].id,
					templateSnapshotId: props.invites[nextIndex].templateSnapshotId,
					appointmentId: props.invites[nextIndex].appointmentId,
					diseaseId: props.invites[nextIndex].diseaseId,
					patientId: props.invites[nextIndex].patientId,
				},
			});
		} else {
			props.afterSubmit && props.afterSubmit(props.user);
		}
	};

	const redirectToLogin = () => {
		return <Redirect to="/login" />;
	};

	return <div className="patient-register-page__photo">
		{redirect && redirectToLogin()}
		{(props.invites?.length > 0) && (state.activeInviteIndex !== null) && (state.activeInviteIndex >= 0)
			? <div className="panel panel-default" style={{ width: '100%' }}>
				<div className="panel-body">
					{props.invites.map((invite, index) => {
						if (index === state.activeInviteIndex) {
							if (invite.diseaseId > 0) {
								return (
									<>
										<div className="row" style={{ paddingRight: '15px' }}>
											{session
												&& <QuestionnaireButtons
													testSession={session}
													signatureSrc={signatureSrc}
													setSignatureSrc={setSignatureSrc}
													setHasSign={setHasSign}
													copyForwardDisabled={copyForwardDisabled}
													setCopyForwardDisabled={setCopyForwardDisabled}
													form={form}
													updateTestSession={(testSession) => {
														setSession({ ...testSession });
													}}
													updateTestSessionAfterSign={false}
												/>
											}
										</div>
										<br />
										<QuestionnaireForm
											session={session}
											setSession={setSession}
											afterSubmit={setNextInvite}
											onCancel={setNextInvite}
											queryParams={state.queryParams}
											enableSave
											hasSign={hasSigh || Boolean(signatureSrc) || Boolean(!session?.disease?.patientSign)}
											setHasSign={setHasSign}
											signatureSrc={signatureSrc}
											setSignatureSrc={setSignatureSrc}
											copyForwardDisabled={copyForwardDisabled}
											setForm={setForm}
											nextBtnOnClick={setNextInvite}
										/>
										<button
											type="button"
											className="btn btn-primary pull-right"
											onClick={() => { setRedirect(true); }}
										>
											Go to dashboard
										</button>
									</>
								);
							}
							return (
								<>
									<div className="row text-center mt10">
										<div className="col-sm-12">
											<h4>
												{props.invites[state.activeInviteIndex].name
													? props.invites[state.activeInviteIndex].name
													: null}
											</h4>
										</div>
									</div>
									<div className="row">
										<div className="col-sm-6">
											{props.invites[state.activeInviteIndex].templateSnapshot?.description
												? props.invites[state.activeInviteIndex].templateSnapshot?.description
												: null}
										</div>
									</div>
									<br />
									<ConsentFormSwitcher
										showPdf={false}
										queryParams={state.queryParams}
										onAccept={setNextInvite}
										onCancel={setNextInvite}
										patientFileId={props.invites[state.activeInviteIndex].patientFileId}
										enableSave
									/>
									<button
										type="button"
										className="btn btn-primary pull-right"
										onClick={() => { setRedirect(true); }}
									>
										Go to dashboard
									</button>
								</>
							);
						}
						return null;
					})}
				</div>
			</div>
			: <Loader />
		}
	</div>;
};
